import React, { useState, useEffect, useRef } from 'react';
import { cn } from '../../utils/cn';

interface OptimizedImageProps {
  src: string;
  alt: string;
  className?: string;
  loading?: 'lazy' | 'eager';
  sizes?: string;
  priority?: boolean;
}

export const OptimizedImage: React.FC<OptimizedImageProps> = ({
  src,
  alt,
  className = '',
  loading = 'lazy',
  sizes = '100vw',
  priority = false,
}) => {
  const [isIntersecting, setIsIntersecting] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const imgRef = useRef<HTMLDivElement>(null);

  // Extract filename without extension and path
  const filename = src.replace(/^.*\//, '').replace(/\.[^/.]+$/, '');
  const basePath = '/images/optimized';

  // Generate srcset for different sizes
  const generateSrcSet = (extension: string) => {
    const sizes = [640, 1024, 1920, 2560];
    return sizes
      .map(size => `${basePath}/${filename}-${size}w.${extension} ${size}w`)
      .join(', ');
  };

  // Use Intersection Observer for lazy loading
  useEffect(() => {
    if (priority || loading === 'eager') {
      setIsIntersecting(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsIntersecting(true);
          observer.disconnect();
        }
      },
      {
        rootMargin: '50px',
      }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => {
      if (observer) {
        observer.disconnect();
      }
    };
  }, [priority, loading]);

  return (
    <div ref={imgRef} className={cn('relative overflow-hidden', className)}>
      {/* Placeholder blur */}
      {!hasLoaded && (
        <img
          src={`${basePath}/${filename}-placeholder.jpg`}
          alt=""
          className="absolute inset-0 w-full h-full object-cover filter blur-lg scale-110"
          aria-hidden="true"
        />
      )}

      {/* Main image with picture element for format selection */}
      {isIntersecting && (
        <picture
          className={cn(
            'transition-opacity duration-300',
            hasLoaded ? 'opacity-100' : 'opacity-0'
          )}
        >
          {/* WebP sources */}
          <source
            type="image/webp"
            srcSet={generateSrcSet('webp')}
            sizes={sizes}
          />
          
          {/* JPEG fallback */}
          <source
            type="image/jpeg"
            srcSet={generateSrcSet('jpg')}
            sizes={sizes}
          />
          
          {/* Fallback img tag */}
          <img
            src={`${basePath}/${filename}-1920w.jpg`}
            alt={alt}
            className={cn('w-full h-full object-cover', className)}
            loading={loading}
            onLoad={() => setHasLoaded(true)}
          />
        </picture>
      )}
    </div>
  );
};